import cn from 'clsx';
import {useId, useState} from 'react';

import useHideMenu from '@/hooks/useHideMenu';
import type {TipData} from '@/types/experienceScore';

import TipItem from './TipItem';
import TipNotificationCountButton from './TipNotificationCountButton';
import TipNotificationExpandedLayer from './TipNotificationExpandedLayer';

interface TipNotificationStackProps {
    tips: TipData[];
    className?: string;
}

const TipNotificationStack = ({tips, className}: TipNotificationStackProps) => {
    const panelId = useId();
    const [expanded, setExpanded] = useState(false);
    const hasMultiple = tips.length > 1;
    // A refresh can shrink the list while the layer is open.
    const isMenuHidden = useHideMenu(expanded && hasMultiple);
    const isOpen = expanded && hasMultiple && isMenuHidden;

    if (tips.length === 0) return null;

    return (
        <>
            <div className={cn('relative w-full', hasMultiple && 'pb-3', isOpen && 'invisible', className)}>
                {hasMultiple && (
                    <div className='pointer-events-none absolute inset-x-4 inset-y-0 bottom-3 translate-y-3 rounded-xl bg-light-silver dark:bg-orion-2' />
                )}
                <div className='relative'>
                    <TipItem tip={tips[0]} className={cn(hasMultiple && 'pr-14')} />
                    {/* A sibling, not a child: the card itself is a button when the tip has an action. */}
                    {hasMultiple && (
                        <div className='absolute top-4.5 right-4.5'>
                            <TipNotificationCountButton
                                count={tips.length}
                                expanded={isOpen}
                                controlsId={panelId}
                                onClick={() => setExpanded(true)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <TipNotificationExpandedLayer id={panelId} open={isOpen} onClose={() => setExpanded(false)} tips={tips} />
        </>
    );
};

export default TipNotificationStack;
